import { CreateFileLayoutRequest } from "../../services/data-kind/create-file-layout/CreateFileLayoutRequest";
import type { Result } from "../../services/if-definitions/update-if-definitions/UpdateIFDefinitionsResponse";
import type {
  Project,
  ProjectTcktInfo,
  Src,
  Dst,
  DuplicatedIdInfo,
  LayoutFileExists,
} from "../../services/if-definitions";
import { ErrorSpreadsheetResult } from "../../util";
import { DeploymentRequestScreenProcessingDivisionType } from "../../util/general-code/enums/general-codes";
import { Option } from "../../util/option";
import { DisplayColumn, ErrorMessage } from "../../util/spreadsheet-data-utils";
import {
  GetIfDefinitionReviewDiffsResponse,
  ReviewDiffRowInfo,
} from "../../services/if-definitions/get-if-definition-review-diffs/GetIfDefinitionReviewDiffsResponse";
import { REVIEW_DIFF_TARGET } from "./InterfaceListPageConstant";

export type InterfaceListPageState = {
  authorized: boolean;
  alertMessage: {
    successMessage?: AlertMessage[];
    errorMessage?: AlertMessage[];
    warningMessage?: AlertMessage[];
  };
  prevActionSuccessMessage: string;
  title?: string;
  projectName?: string;
  isPendingDisplay: boolean;
  isHistoryDisplay: boolean;
  issuedRowIdx: number;
  issuedEventName: string;
  deploymentMode?: string;
  dfnSetVerNum: string;
  dplymntDate: string;
  dplymntTime: string;
  searchForm: SearchForm;
  spreadsheetSelectedRowIndex: number | undefined;
  comment: {
    commentDescription: string;
    selectedEvent: string;
    ifList: Interface;
  };
  ifList: Interface[];
  editedIfList: Interface[];
  retentionEditedIfList: Map<string, any>;
  interfaceListGeneralCodeState: GeneralCode;
  edhNotificationType: EdhNotificationType;
  displayColumn: DisplayColumn[];
  createFileLayoutConfirmDialogState: CreateFileLayoutConfirmDialogState;
  isSaveAuth: boolean;
  reviewDiffState: ReviewDiffState;
};

export type AlertMessage = {
  id?: string | undefined;
  message: string;
};

export type SearchForm = {
  srcDipByRgnOrDip: string;
  srcIfId: string;
  srcInbdId: string;
  srcInbdCpntId: string;
  dstDipByRgnOrDip: string;
  dstIfId: string;
  dstOtbdId: string;
  dstOtbdCpntId: string;
  interfaceNameEnglish: string;
  interfaceNameJapanese: string;
};

export type Interface = {
  idx: Number;
  isAddRow: boolean;
  isInternalDIP?: boolean;
  existSrcDsgnCmnt: boolean;
  existDstDsgnCmnt: boolean;
  cancelledRequirement?: boolean;
  srcAction?: string;
  dstAction?: string;
  srcDipSelect?: boolean;
  dstDipSelect?: boolean;
  srcInterruptType?: string;
  dstInterruptType?: string;
  shouldShowInterruptTypeFlag?: boolean;
  srcFileList: Option[];
  dstFileList: Option[];
  srcInboundOutputFileList: Option[];
  dstInboundOutputFileList?: Option[];
  updateDateTime: string;
  srcDplymntReqSeqNum?: string;
  srcDplymntDtime?: string;
  dstDplymntReqSeqNum?: string;
  dstDplymntDtime?: string;
  project: Project;
  projectTcktInfo: ProjectTcktInfo;
  src: Src;
  dst: Dst;
  information: Information;
  errorColumnList: string[];
  errorMessage: string;
  messageList: ErrorMessage[];
  srcMetaOnlyFlag?: boolean;
  dstMetaOnlyFlag?: boolean;
  partialRestoreRowEdited?: boolean;
  partialRestoreCellEdited?: Record<string, boolean>;
  reviewDiffInfo?: ReviewDiffRowInfo;
};
export type Information = {
  val: string;
  col: string[];
  duplicatedIdList: DuplicatedIdInfo[];
  layoutFileExistanceList?: LayoutFileExists[];
};
export type GeneralCode = {
  commonConditionFlagType: Option[];
  securityContentsType: Option[];
  sendDatalakeType: Option[];
  fileExtensionType: Option[];
  referenceModeType: Option[];
  godReferenceModeType: Option[];
  reviewActionType: Option[];
  reviewingActionType: Option[];
  designCompletedActionType: Option[];
  interfaceRequirementType: Option[];
  interfaceUpdateRequirementType: Option[];
  edhNotificationType: Option[];
  edhPrtclType: Option[];
  inboundEdhProtocolList?: Option[];
  outboundEdhProtocolList?: Option[];
  ptnrType: Option[];
  chnTypeType: Option[];
  srcEdiChainTypeList?: Option[];
  dstEdiChainTypeList?: Option[];
  fileTypeType: Option[];
  ifCycleType: Option[];
  snapshotIncrementalDeltaType: Option[];
  dataScopeType: Option[];
  encriptionType: Option[];
  securityInformationLevelType: Option[];
  tcktStsTypePrjPrgrs: Option[];
  dipByRgnOrDipList: Option[];
  dipByRegionList?: Option[];
  retentionPeriodDays: Option[];
  ediPollingAdaptorType: Option[];
  interruptType: Option[];
};

export type SpreadsheetState = {
  ifList: InterfaceSpreadsheetRow[];
  interfaceListGeneralCodeState: GeneralCode;
  edhNotificationType: EdhNotificationType;
  mode: string;
  // show pending flag for deployment request reference mode
  showPending?: boolean;
  showColumnPresets?: {
    srcInbound: string[];
    dstOutbound: string[];
    basicInformation: string[];
    crossDomain: string[];
  };

  deploymentRequestScreenProcessingDivisionType?: string;
  isDplymntDetailScreen: boolean;
};

export const srcInboundColumnPresets = [
  { label: "External Partner Linkage", value: "col_src_edi" },
  { label: "EDH", value: "col_src_edh" },
  { label: "ETL Job", value: "col_src_talend" },
  { label: "Meta/Security", value: "col_src_meta" },
];

export const dstOutboundColumnPresets = [
  { label: "EDH", value: "col_dst_edh" },
  { label: "ETL Job", value: "col_dst_talend" },
  { label: "External Partner Linkage", value: "col_dst_edi" },
  { label: "Meta/Security", value: "col_dst_meta" },
];

export const crossDomainColumnPresets = [
  { label: "Src Outbound", value: "col_src_outbound" },
  { label: "Cross Domain", value: "col_cross" },
];

export const crossDomainColumnPresetsHasDstInbound = [
  { label: "Src Outbound", value: "col_src_outbound" },
  { label: "Cross Domain", value: "col_cross" },
  { label: "Dst Inbound", value: "col_dst_inbound" },
];

export const basicColumnPresets = [
  { label: "Basic Information", value: "col_basic_information" },
];

export type InterfaceSpreadsheetRow = Interface & ErrorSpreadsheetResult;

export type EdhNotificationType = {
  [key: string]: {
    edhNotificationTypeList: Option[];
  };
};

export type CreateFileLayoutConfirmDialogState = {
  isShow: boolean;
  createFileLayoutRequest?: CreateFileLayoutRequest;
  issuedRowIdx?: number;
  issuedEventName?: string;
};

export type PageNewState =
  | InterfaceListPageState
  | ((prevState: InterfaceListPageState) => InterfaceListPageState);

export type EditedCell = { path: string; value: string };

export type PendingPartialRestore = {
  failedRows: Array<{
    index: number;
    ifReqId: string;
    edits: EditedCell[];
    error: Result;
  }>;
};

export type ReviewDiffTarget =
  (typeof REVIEW_DIFF_TARGET)[keyof typeof REVIEW_DIFF_TARGET];

export type ReviewDiffState = {
  selectedTarget: ReviewDiffTarget;
  response: GetIfDefinitionReviewDiffsResponse | null;
};
